import { useMemo } from "react";
import { Package, Store, Boxes as BoxesIcon, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fuzzyScoreFields } from "@shared/fuzzy";
import {
  matchesAssist,
  type SearchAssist,
  buildProducerIndex,
  itemFields,
  producerFields,
  boxFields,
  producerLabel,
  sellerLabel,
} from "@shared/searchFields";

export type SearchItem = {
  id: string;
  name: string;
  sellerId?: string | null;
  category?: string | null;
  isGlutenFree?: number | null;
  seller?: { displayName?: string | null; firstName?: string | null; lastName?: string | null } | null;
};

export type SearchProducer = {
  id: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  bio?: string | null;
  sellerType?: string | null;
  zipCode?: string | null;
};
export type SearchBox = {
  id: string;
  title: string;
  sellerNames: string[];
  itemCount: number;
  items: { name: string }[];
};

const MAX_PER_GROUP = 4;


export function BrowseSearchResults({
  query,
  items,
  producers,
  boxes,
  loading = false,
  assist = null,
  assistLoading = false,
  onPickItem,
  onPickProducer,
  onPickBoxes,
  onSeeAll,
  onRequest,
}: {
  query: string;
  items: SearchItem[];
  producers: SearchProducer[];
  boxes: SearchBox[];
  loading?: boolean;
  assist?: SearchAssist | null;
  assistLoading?: boolean;
  onPickItem: (id: string) => void;
  onPickProducer: (id: string) => void;
  onPickBoxes: () => void;
  onSeeAll: (mode: "products" | "producers" | "boxes") => void;
  onRequest?: () => void;
}) {
  const q = query.trim();

  const { itemHits, producerHits, boxHits, idx } = useMemo(() => {
    const rank = <T,>(rows: T[], score: (r: T) => number | null) =>
      rows
        .map((r) => ({ r, s: score(r) }))
        .filter((x): x is { r: T; s: number } => x.s != null)
        .sort((a, b) => b.s - a.s)
        .map((x) => x.r);

    const idx = buildProducerIndex(items);

    return {
      itemHits: rank(items, (i) => fuzzyScoreFields(q, itemFields(i))),
      producerHits: rank(producers, (p) => fuzzyScoreFields(q, producerFields(p, idx))),
      boxHits: rank(boxes, (b) => fuzzyScoreFields(q, boxFields(b))),
      idx,
    };
  }, [q, items, producers, boxes]);

  // Broadened matches, excluding anything the direct query already found, so
  // the AI layer only ever ADDS to the honest local result set.
  const assistHits = useMemo(() => {
    if (!assist || (assist.terms.length === 0 && assist.dietary.length === 0)) return [];
    const direct = new Set(itemHits.map((i) => i.id));
    return items.filter((i) => !direct.has(i.id) && matchesAssist(assist, i));
  }, [assist, items, itemHits]);

  const total = itemHits.length + producerHits.length + boxHits.length + assistHits.length;

  return (
    // Keep focus on the input so a tap here fires onClick before onBlur closes us.
    <div
      onMouseDown={(e) => e.preventDefault()}
      className="absolute left-0 right-0 top-full z-[60] mt-1 max-h-[60vh] overflow-y-auto overscroll-contain rounded-xl border border-border bg-popover shadow-2xl"
    >
      {total === 0 ? (
        loading || assistLoading ? (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">Searching…</div>
        ) : (
          // The dead end is the most informative moment we get, and the shopper
          // is right here — so ask, rather than just apologising.
          <div className="px-3 py-4">
            <p className="px-1 text-center text-sm text-muted-foreground">
              Nobody's selling “{q}” yet.
            </p>
            {onRequest && (
              <Button size="sm" className="mt-3 w-full" onClick={onRequest}>
                Request it, or name someone who makes it
              </Button>
            )}
          </div>
        )
      ) : (
        <div className="py-1">
          {itemHits.length > 0 && (
            <Group
              icon={<Package className="h-3.5 w-3.5" />}
              label="Listings"
              count={itemHits.length}
              onSeeAll={() => onSeeAll("products")}
            >
              {itemHits.slice(0, MAX_PER_GROUP).map((i) => (
                <Row
                  key={i.id}
                  title={i.name}
                  subtitle={sellerLabel(i.seller) || undefined}
                  onClick={() => onPickItem(i.id)}
                  testid={`search-item-${i.id}`}
                />
              ))}
            </Group>
          )}

          {producerHits.length > 0 && (
            <Group
              icon={<Store className="h-3.5 w-3.5" />}
              label="Growers & Makers"
              count={producerHits.length}
              onSeeAll={() => onSeeAll("producers")}
            >
              {producerHits.slice(0, MAX_PER_GROUP).map((p) => {
                // Say why this maker matched. "Sells Fresh Summer Tomatoes"
                // earns its place in the list; "maker" doesn't, and a row that
                // can't explain itself is the one that reads as a wrong result.
                const match = (idx.sells.get(p.id) ?? []).find(
                  (n) => fuzzyScoreFields(q, [[n, 1]]) != null,
                );
                const cat = match
                  ? null
                  : [...(idx.cats.get(p.id) ?? [])].find(
                      (c) => fuzzyScoreFields(q, [[c, 1, "strict"]]) != null,
                    );
                return (
                  <Row
                    key={p.id}
                    title={producerLabel(p)}
                    subtitle={
                      match
                        ? `Sells ${match}`
                        : cat
                          ? `Sells ${cat}`
                          : p.sellerType || undefined
                    }
                    onClick={() => onPickProducer(p.id)}
                    testid={`search-producer-${p.id}`}
                  />
                );
              })}
            </Group>
          )}

          {boxHits.length > 0 && (
            <Group
              icon={<BoxesIcon className="h-3.5 w-3.5" />}
              label="Boxes"
              count={boxHits.length}
              onSeeAll={onPickBoxes}
            >
              {boxHits.slice(0, MAX_PER_GROUP).map((b) => (
                <Row
                  key={b.id}
                  title={b.title}
                  subtitle={`${b.itemCount} item${b.itemCount === 1 ? "" : "s"}${b.sellerNames.length ? ` · ${b.sellerNames.join(", ")}` : ""}`}
                  onClick={onPickBoxes}
                  testid={`search-box-${b.id}`}
                />
              ))}
            </Group>
          )}
          {assistHits.length > 0 && (
            <Group
              icon={<Sparkles className="h-3.5 w-3.5" />}
              label={`Related to "${q}"`}
              count={assistHits.length}
              onSeeAll={() => onSeeAll("products")}
            >
              {assistHits.slice(0, MAX_PER_GROUP).map((i) => (
                <Row
                  key={i.id}
                  title={i.name}
                  subtitle={sellerLabel(i.seller) || undefined}
                  onClick={() => onPickItem(i.id)}
                  testid={`search-assist-${i.id}`}
                />
              ))}
            </Group>
          )}
        </div>
      )}
    </div>
  );
}

function Group({
  icon,
  label,
  count,
  onSeeAll,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  onSeeAll: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="py-1">
      <div className="flex items-center gap-1.5 px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      {children}
      {count > MAX_PER_GROUP && (
        <button
          type="button"
          onClick={onSeeAll}
          className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium text-primary hover:bg-muted"
        >
          See all {count}
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function Row({
  title,
  subtitle,
  onClick,
  testid,
}: {
  title: string;
  subtitle?: string;
  onClick: () => void;
  testid?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-muted"
    >
      <span className="line-clamp-1 text-sm font-medium text-foreground">{title}</span>
      {subtitle && <span className="line-clamp-1 text-xs text-muted-foreground">{subtitle}</span>}
    </button>
  );
}
